import React, { useEffect, useRef } from 'react';
import { PartQuote } from 'types';
import { ContextMenuContainer, MenuItem, MenuOverlay } from './QuoteRowContextMenu.styles';
import { DetailIcon } from 'icons/indexed-icons/DetailIcon';
import TrashIcon from 'icons/indexed-icons/TrashNew';
import  CartIcon  from 'icons/indexed-icons/ShoppingCartAdd';

interface QuoteRowContextMenuProps {
  quote: PartQuote;
  position: { top: number; left: number } | null;
  onClose: () => void;
  onAddToCart: (quote: PartQuote) => void;
  onViewDetails: (quote: PartQuote) => void;
  onDeleteQuote?: (quote: PartQuote) => void;
}

export const QuoteRowContextMenu: React.FC<QuoteRowContextMenuProps> = ({
  quote,
  position,
  onClose,
  onAddToCart,
  onViewDetails,
  onDeleteQuote
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (position) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [position, onClose]);

  if (!position) return null;

  const status = quote.status?.toLowerCase() || '';
  const showAddToCart = status === 'pending' || status === 'expired';
  const showViewDetails = status === 'pending';
  const showDeleteQuote = status === 'pending' || status === 'expired' || status === 'in progress';

  const handleAddToCart = () => {
    onAddToCart(quote);
    onClose();
  };

  const handleViewDetails = () => {
    onViewDetails(quote);
    onClose();
  };

  const handleDeleteQuote = () => {
    onDeleteQuote(quote);
    onClose();
  };

  return (
    <>
      <MenuOverlay onClick={onClose} />
      <ContextMenuContainer ref={menuRef} top={position.top} left={position.left}>
        {showAddToCart && (
          <MenuItem onClick={handleAddToCart} title="Add quote items to cart">
            <CartIcon />
            <span>Add to cart</span>
          </MenuItem>
        )}
        {showViewDetails && (
          <MenuItem onClick={handleViewDetails} title="View quote details">
            <DetailIcon />
            <span>View Quote Details</span>
          </MenuItem>
        )}
        {showDeleteQuote && (
          <MenuItem onClick={handleDeleteQuote} title="Delete this quote">
            <TrashIcon />
            <span>Delete quote</span>
          </MenuItem>
        )}
      </ContextMenuContainer>
    </>
  );
};

export default QuoteRowContextMenu;
